import { isAxiosError } from 'axios';
import { isUndefined } from 'lodash';
import { I18nUtils } from '@medable/i18n-core';
import { getApiInstance } from '../../../helpers/api.helpers';
export const getParticipantFormInitialValues = (participant, currentLocale = 'en-US') => {
    return {
        email: participant?.c_email ?? undefined,
        language: participant?.c_locale
            ? { label: I18nUtils.getLanguageLabel(participant.c_locale, currentLocale, true), value: participant?.c_locale }
            : { label: '', value: '' },
        participantNumber: participant?.c_number ?? undefined,
    };
};
export const shouldDisplayParticipantNumber = (org) => {
    if (org?.c_automatic_participant_id_generation || org?.c_participantid_on_visit) {
        return false;
    }
    return true;
};
// The deterministic endpoint (AXONCONFIG-5249 / SSP-4104) creates the participant and returns the
// created c_public_user directly. Not every environment has it deployed yet, so a 404 falls back
// to the legacy list-property POST below.
const createParticipantDeterministic = async ({ environment, site, body, }) => {
    const path = `v2/routes/c_site/${site?._id}/participants/invite`;
    const response = await getApiInstance(environment).post(path, body);
    return response?.data;
};
// Legacy fallback: POSTing to the c_subjects list property returns a *list* of the site's subjects,
// not the created record. Identify the new participant by matching the unique fields we just
// submitted (c_email / c_number) instead of trusting sort order — re-enrolling an existing
// c_public_user keeps its original `created`, so `sort: { created: -1 }` can return a different,
// newer participant. Only fall back to "newest" when neither field was provided (auto-generated
// IDs), where a brand-new subject is genuinely the most recently created — this remains racy under
// concurrent creates, which is why the deterministic endpoint above is preferred.
const createParticipantLegacy = async ({ environment, account, site, body, }) => {
    const path = `account/${account._id}/c_sites/${site?._id}/c_subjects`;
    const matchWhere = {
        ...(body.c_email && { c_email: body.c_email }),
        ...(body.c_number && { c_number: body.c_number }),
    };
    const params = Object.keys(matchWhere).length
        ? { limit: 1, skip: 0, where: JSON.stringify(matchWhere) }
        : { limit: 1, skip: 0, sort: JSON.stringify({ created: -1 }) };
    const createdParticipant = await getApiInstance(environment).post(path, body, { params });
    return createdParticipant?.data?.data?.[0];
};
export const createParticipant = async ({ environment, org, account, site, form }) => {
    const body = {
        c_study: org?._id,
        c_locale: I18nUtils.i18nToCortexFormat(form.language.value),
        ...(!org?.c_no_pii && form?.email && { c_email: form.email }),
        ...(form?.participantNumber && {
            c_number: form.participantNumber,
        }),
    };
    let participant;
    try {
        participant = await createParticipantDeterministic({ environment, site, body });
    }
    catch (error) {
        // Only fall back when the deterministic endpoint is not deployed (404). Real failures
        // (e.g. 400 validation, 403 access denied) must propagate so the modal surfaces them.
        if (!isAxiosError(error) || error.response?.status !== 404) {
            throw error;
        }
        participant = await createParticipantLegacy({ environment, account, site, body });
    }
    // Guardrail: the request succeeded but the response did not yield a participant we can identify
    // (empty list / unexpected shape). Surface this as an error so the modal stays open with the
    // filled-in values and the user can retry, instead of silently doing nothing.
    if (!participant?._id) {
        throw new Error('Participant creation did not return a valid participant');
    }
    return participant;
};
export const resendParticipantInvite = async ({ org, site, environment, participant, studyInviteCode, sendInviteEmail = false, }) => {
    const defaultGroupId = org?.c_default_subject_group?._id;
    const inviteData = {
        c_site: site._id,
        c_public_user: participant._id,
        c_group: defaultGroupId,
        locale: participant.c_locale,
        c_email: sendInviteEmail ? participant.c_email : undefined,
        c_study_code: studyInviteCode,
    };
    const inviteResponse = await getApiInstance(environment).get(`/routes/resend_invite`, {
        params: inviteData,
    });
    return inviteResponse?.data;
};
export const updateParticipant = async ({ environment, account, site, form, participantId, }) => {
    const path = `account/${account._id}/c_sites/${site?._id}/c_subjects/${participantId}`;
    const body = [
        {
            op: 'set',
            value: {
                ...(form.email && { c_email: form.email }),
                ...(!isUndefined(form.caregiverEproEnabled) && { c_caregiver_epro_enabled: form.caregiverEproEnabled }),
            },
        },
    ];
    return await getApiInstance(environment).patch(path, body);
};
export const unsetParticipantEmail = async ({ environment, participantId, }) => {
    const path = `c_public_users/${participantId}`;
    const body = [{ op: 'unset', value: { c_email: 1 } }];
    return await getApiInstance(environment).patch(path, body);
};
